import React, { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  SimpleGrid,
  Spinner,
  Switch,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import PageShell from "../layout/PageShell";

const WINDOW_DOC = doc(db, "systemConfig", "posWindow");
const ICON_DOC = doc(db, "systemConfig", "appIcon");

const ENVS = ["DEV", "ESSAI", "PROD"] as const;
type PosEnv = (typeof ENVS)[number];
type ModuleFlags = Partial<Record<PosEnv, boolean>>;
type ModuleMap = Record<string, ModuleFlags>;

type ModuleNode = {
  label: string;
  key?: string;
  locked?: boolean;
  children?: ModuleNode[];
};

const POS_MODULES: ModuleNode[] = [
  { label: "POS", locked: true },
  { label: "KDS", key: "navKds" },
  { label: "Reservations", key: "navReservations" },
  { label: "Customers", key: "navCustomers" },
  { label: "Analytics", key: "navAnalytics" },
  { label: "Layout", key: "navLayout" },
  {
    label: "Menus",
    locked: true,
    children: [
      { label: "Menus", key: "menusMenus" },
      { label: "Advance Option", key: "menusOptions" },
      { label: "Item Tags", key: "menusTags" },
      { label: "Categories", key: "menusCategories" },
      { label: "Order Rules", key: "menusOrderRules" },
      { label: "Menu display", key: "menusDisplay" },
      { label: "Recipe", key: "menusRecipes" },
      { label: "Digital Platform", key: "menusDigitalPlatform" },
      { label: "Uber Eats", key: "menusUberEats" },
    ],
  },
  {
    label: "Settings",
    locked: true,
    children: [
      { label: "Organization", key: "settingsOrganization" },
      {
        label: "Store details",
        key: "settingsStoreDetails",
        children: [
          { label: "Store Information", key: "settingsStoreInformation" },
          { label: "Workstation registration", key: "settingsWorkstationSetup" },
          { label: "Workstation sync", key: "settingsWorkstationSync" },
          { label: "Store Logo", key: "settingsStoreLogo" },
          { label: "Screen Idle Time", key: "settingsIdleTimeout" },
          { label: "Location control", key: "settingsLocationControl" },
          { label: "Store Hours", key: "settingsStoreHours" },
          { label: "Special Hours", key: "settingsSpecialHours" },
        ],
      },
      {
        label: "Store Operation",
        key: "settingsStoreOperation",
        children: [
          { label: "Store Type", key: "settingsStoreType" },
          { label: "Taxes & Fees", key: "settingsTaxesFees" },
          { label: "Guest groups", key: "settingsGuestGroups" },
          { label: "Table Ordering Setting", key: "settingsTableOrdering" },
          { label: "Session & Display Settings", key: "settingsSessionDisplay" },
          { label: "Keypad popup at login", key: "settingsKeypadPopup" },
          { label: "POS Dashboard Cards", key: "settingsDashboardCards" },
          { label: "POS Interface Theme", key: "settingsPosTheme" },
          { label: "Customer screen", key: "settingsCustomerExperience" },
          { label: "Table View", key: "settingsTableView" },
          { label: "Reservations", key: "settingsOperationReservations" },
          { label: "Offline", key: "settingsOffline" },
          { label: "End of Day", key: "settingsEod" },
          { label: "Subscription", key: "settingsSubscription" },
          { label: "Training Mode", key: "settingsTrainingMode" },
          { label: "Regional & Integration Settings", key: "settingsRegionalIntegration" },
        ],
      },
      {
        label: "Payment",
        key: "settingsPayment",
        children: [
          { label: "Payment Settings", key: "settingsPaymentSettings" },
        ],
      },
      { label: "Payment Processor", key: "settingsPaymentProcessor" },
      { label: "Print", key: "settingsPrint" },
      { label: "Webhooks", key: "settingsWebhooks" },
      { label: "Users", key: "settingsUsers" },
      { label: "KDS", key: "settingsKds" },
      { label: "Voice", key: "settingsVoice" },
      {
        label: "Data Management",
        key: "settingsOthers",
        children: [
          { label: "Data Management", key: "settingsDataManagement" },
          { label: "Export Data", key: "settingsExportData" },
        ],
      },
      { label: "Online ordering", key: "settingsOnlineOrdering" },
      {
        label: "QR Table",
        key: "settingsQrTable",
        children: [
          { label: "Tables", key: "settingsTables" },
        ],
      },
      { label: "Cloud Sync", key: "settingsCloudSync" },
      { label: "OEM", key: "settingsOem" },
      { label: "3PL", key: "settingsThreePl" },
      { label: "Kiosk", key: "settingsKiosk" },
      { label: "SD", key: "settingsSd" },
      { label: "SR", key: "settingsSr" },
      { label: "LN", key: "settingsLn" },
      { label: "Uber", key: "settingsUber" },
    ],
  },
  {
    label: "Printers",
    locked: true,
    children: [
      { label: "Printers", key: "printersPrinters" },
      { label: "Settings", key: "printersSettings" },
      { label: "Logs", key: "printersLogs" },
      { label: "Discovery", key: "printersDiscovery" },
    ],
  },
  {
    label: "Cloud MEV",
    locked: true,
    children: [
      { label: "MEV Order Monitor", key: "cloudMevMonitor" },
      { label: "Status", key: "cloudMevStatus" },
      { label: "Certificate", key: "cloudMevCertificate" },
      { label: "User Report", key: "cloudMevUserReport" },
      { label: "Logs", key: "cloudMevLogs" },
      { label: "How to", key: "cloudMevHowTo" },
    ],
  },
];

const ICON_SERIES = [
  { id: "original_navy", label: "Original navy" },
  { id: "midnight_blue", label: "Midnight blue" },
  { id: "deep_charcoal", label: "Deep charcoal" },
  { id: "pos_slate", label: "POS slate" },
  { id: "original_navy_br", label: "Original navy bright" },
  { id: "midnight_blue_br", label: "Midnight blue bright" },
  { id: "deep_charcoal_br", label: "Deep charcoal bright" },
  { id: "pos_slate_br", label: "POS slate bright" },
];

const PosWindowSettings: React.FC = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [iconSaving, setIconSaving] = useState(false);
  const [showAddressBar, setShowAddressBar] = useState(false);
  const [modules, setModules] = useState<ModuleMap>({});
  const modulesRef = useRef<ModuleMap>({});
  const [iconSeries, setIconSeries] = useState("");
  const [pendingSeries, setPendingSeries] = useState<string | null>(null);
  const [confirmFrom, setConfirmFrom] = useState("");
  const cancelRef = useRef<HTMLButtonElement>(null);
  const panelBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("#d5e4ea", "gray.600");

  useEffect(() => {
    let cancelled = false;
    Promise.all([getDoc(WINDOW_DOC), getDoc(ICON_DOC)])
      .then(([windowSnap, iconSnap]) => {
        if (cancelled) return;
        const windowData = windowSnap.exists() ? windowSnap.data() : null;
        setShowAddressBar(windowData?.showAddressBar === true);
        const savedModules = windowData?.modules;
        const nextModules = savedModules && typeof savedModules === "object" ? savedModules as ModuleMap : {};
        modulesRef.current = nextModules;
        setModules(nextModules);
        const series = iconSnap.exists() ? iconSnap.data()?.series : "";
        setIconSeries(typeof series === "string" ? series : "");
      })
      .catch((error) => {
        console.error("POS window settings could not be loaded", error);
        toast({ title: "Could not load POS window settings", status: "error", duration: 3000 });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const flagsFor = (key: string): Record<PosEnv, boolean> => ({
    DEV: modules[key]?.DEV !== false,
    ESSAI: modules[key]?.ESSAI !== false,
    PROD: modules[key]?.PROD !== false,
  });

  const saveModule = async (key: string, env: PosEnv, checked: boolean) => {
    const previous = modulesRef.current;
    const currentFlags = {
      DEV: previous[key]?.DEV !== false,
      ESSAI: previous[key]?.ESSAI !== false,
      PROD: previous[key]?.PROD !== false,
      [env]: checked,
    };
    const next = { ...previous, [key]: currentFlags };
    modulesRef.current = next;
    setModules(next);
    try {
      const payload = {
        [`modules.${key}`]: currentFlags,
        updatedAt: serverTimestamp(),
      };
      try {
        await updateDoc(WINDOW_DOC, payload);
      } catch (error) {
        const code = (error as { code?: string })?.code;
        if (code !== "not-found") throw error;
        await setDoc(WINDOW_DOC, {
          modules: { [key]: currentFlags },
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }
    } catch (error) {
      console.error("POS module setting could not be saved", error);
      modulesRef.current = previous;
      setModules(previous);
      toast({ title: "Could not save the menu setting", status: "error", duration: 3000 });
    }
  };

  const saveAddressBar = async (next: boolean) => {
    const previous = showAddressBar;
    setShowAddressBar(next);
    setSaving(true);
    try {
      await setDoc(WINDOW_DOC, {
        showAddressBar: next,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (error) {
      console.error("POS window settings could not be saved", error);
      setShowAddressBar(previous);
      toast({ title: "Could not save the address bar setting", status: "error", duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const askIconSeries = (series: string) => {
    if (iconSaving || series === iconSeries) return;
    setConfirmFrom(ICON_SERIES.find((item) => item.id === iconSeries)?.label || "");
    setPendingSeries(series);
  };

  const saveIconSeries = async (series: string) => {
    const previous = iconSeries;
    setIconSeries(series);
    setIconSaving(true);
    try {
      await setDoc(ICON_DOC, {
        series,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (error) {
      console.error("App icon could not be saved", error);
      setIconSeries(previous);
      toast({ title: "Could not save the app icon", status: "error", duration: 3000 });
    } finally {
      setIconSaving(false);
    }
  };

  const confirmIconSeries = async () => {
    if (!pendingSeries) return;
    const series = pendingSeries;
    await saveIconSeries(series);
    setPendingSeries(null);
  };

  const pendingIcon = ICON_SERIES.find((series) => series.id === pendingSeries);

  const renderModule = (node: ModuleNode, depth: number) => (
    <Box key={`${depth}-${node.label}`}>
      <Box
        display="grid"
        gridTemplateColumns="minmax(0, 1fr) 112px 112px 112px"
        alignItems="center"
        minH="44px"
        pl={`${depth * 20}px`}
        borderTop="1px solid"
        borderColor={borderColor}
      >
        <Text fontWeight={node.locked ? "600" : "500"}>{node.label}</Text>
        {node.locked || !node.key ? (
          <Text gridColumn="2 / span 3" fontSize="sm" color="gray.600" textAlign="right" pr={2}>Always on</Text>
        ) : (
          ENVS.map((env) => (
            <Checkbox
              key={env}
              size="lg"
              colorScheme="blue"
              isChecked={flagsFor(node.key as string)[env]}
              onChange={(event) => saveModule(node.key as string, env, event.target.checked)}
            >
              {env}
            </Checkbox>
          ))
        )}
      </Box>
      {node.children?.map((child) => renderModule(child, depth + 1))}
    </Box>
  );

  return (
    <PageShell
      title="POS window"
      description="Choose the app icon, show the address in the title bar when you need it for debugging, and choose which POS menus appear in each environment."
    >
      {loading ? <Spinner /> : (
        <>
          <Box bg={panelBg} border="1px solid" borderColor={borderColor} borderRadius="lg" p={5} mb={4}>
            <FormLabel>App icon</FormLabel>
            <Text fontSize="sm" color="gray.600" mb={4}>
              The selected series is used for the POS, the website, and the next desktop build.
            </Text>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
              {ICON_SERIES.map((series) => {
                const selected = iconSeries === series.id;
                return (
                  <Box
                    as="button"
                    type="button"
                    key={series.id}
                    onClick={() => askIconSeries(series.id)}
                    disabled={iconSaving}
                    border="2px solid"
                    borderColor={selected ? "blue.500" : borderColor}
                    borderRadius="lg"
                    p={3}
                    textAlign="left"
                    bg={selected ? "blue.50" : "transparent"}
                  >
                    <img
                      src={`/icon-series/${series.id}_256.png`}
                      alt={series.label}
                      width={96}
                      height={96}
                      style={{ width: 96, height: 96, objectFit: "contain", display: "block" }}
                    />
                    <Text mt={2} fontWeight="600">{series.label}</Text>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>
          <Box bg={panelBg} border="1px solid" borderColor={borderColor} borderRadius="lg" p={5}>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Box mr={4}>
                <FormLabel mb={0}>Show address bar</FormLabel>
                <Text fontSize="sm" color="gray.600">
                  Off by default. When this is on, the POS title bar shows the current page address.
                </Text>
              </Box>
              <Switch
                isChecked={showAddressBar}
                isDisabled={saving}
                onChange={(event) => saveAddressBar(event.target.checked)}
              />
            </FormControl>
          </Box>
          <Box bg={panelBg} border="1px solid" borderColor={borderColor} borderRadius="lg" p={5} mt={4}>
            <FormLabel>POS menus</FormLabel>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Each checkbox shows that item while a store is in that environment. POS, Settings, Menus, Printers, and Cloud MEV stay on.
            </Text>
            <Box>
              {POS_MODULES.map((node) => renderModule(node, 0))}
            </Box>
          </Box>
          <AlertDialog
            isOpen={pendingSeries !== null}
            leastDestructiveRef={cancelRef}
            onClose={() => {
              if (!iconSaving) setPendingSeries(null);
            }}
            isCentered
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader>Change the app icon</AlertDialogHeader>
                <AlertDialogBody>
                  <Text mb={3}>
                    {confirmFrom
                      ? `Change the app icon from ${confirmFrom} to ${pendingIcon?.label || "the selected series"}.`
                      : `Change the app icon to ${pendingIcon?.label || "the selected series"}.`}
                  </Text>
                  <Text mb={2}>This change updates:</Text>
                  <Text>The POS, including the navigation mark, the sign-in screen, and the kitchen display.</Text>
                  <Text>The website icon.</Text>
                  <Text>The next desktop build, including the setup file, the installed program, shortcuts, and the tray icon.</Text>
                  <Text mt={3}>An installed copy keeps its current icon until the next build.</Text>
                </AlertDialogBody>
                <AlertDialogFooter flexDirection="column" gap={3}>
                  <Button
                    width="100%"
                    size="lg"
                    colorScheme="blue"
                    isLoading={iconSaving}
                    onClick={confirmIconSeries}
                  >
                    Change icon
                  </Button>
                  <Button
                    ref={cancelRef}
                    width="100%"
                    size="lg"
                    variant="outline"
                    onClick={() => setPendingSeries(null)}
                    isDisabled={iconSaving}
                  >
                    Cancel
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </>
      )}
    </PageShell>
  );
};

export default PosWindowSettings;
